import React from 'react';
import Helmet from 'react-helmet';
import HtmlParser from 'html-react-parser';
import ReactPlayer from 'react-player';
import SEO from '../../components/SEO';
import Layout from '../../layouts/index';
import { DemoFormLong } from '../../components/DemoFormLong.jsx';
import { DemoFormShort } from '../../components/DemoFormShort.jsx';
import JoinHappyCustomers from '../../components/JoinHappyCustomers';
import MultimedialPanel from '../../components/MultimedialPanel';

const Home = (props) => {

    const location = props.location;

    return (
        <Layout  bodyClass="page-integrations performance" ontouchstart>
            <SEO title="Performance Management | ChartHop | People Analytics, Visualized" />
            <Helmet>
                <meta
                    name="description"
                    content="Leverage performance data to make more thoughtful, unbiased, data-informed decision about compensation, promotions, and hiring - all in one place."
                />
                <meta property="og:url" content={`https://www.charthop.com${location.pathname}`} />
                <meta property="og:title" content="Performance Management | ChartHop | People Analytics, Visualized" />
                <meta property="og:description" content="Leverage performance data to make more thoughtful, unbiased, data-informed decision about compensation, promotions, and hiring - all in one place." />
                <meta property="og:image" content={`https://www.charthop.com/home/meta-performance.png`} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en_US" />
                <meta property="og:site_name" content="ChartHop" />
                {/* Twitter Card tags */}
                <meta name="twitter:title" content="Performance Management | ChartHop | People Analytics, Visualized" />
                <meta name="twitter:description" content="Leverage performance data to make more thoughtful, unbiased, data-informed decision about compensation, promotions, and hiring - all in one place." />
                <meta name="twitter:image" content={`https://www.charthop.com/home/meta-performance.png`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@charthop" />
                <meta name="twitter:creator" content="@charthop" />
            </Helmet>            

            {/* HERO CONTENT */}
            <section className="intro container">
                <div className="row py-5 align-items-center">
                    <div className="col-md-6 p-xs-2 mb-3 mb-md-0 pr-md-2 pr-lg-4">
                        <h1>Performance reviews, in context</h1>
                        <p className="header-p pb-1 pt-1">Continuous performance management connected to all of your people data and processes</p>
                        <div className="splitText"><DemoFormShort source={"platform-performance-demo-header"} /></div>                     
                    </div>
                    <div className="reactPlayerContainer col-md-6 order-md-1 padding-top-video-one">                        
                        <img className="imageBoxShaddow fullSegmentImage mx-auto" src={'/product/2105-refresh/performance-expectations.png'} alt="Performance Review Org Chart" />

                    </div>
                </div>
            </section>

            <section className="sectionSpacing flex-centeredColumn backgroundGradientContainer" >
                <h2 className="text-center mt-0 sectionSpacing-Margin">Performance management for the entire employee lifecycle.</h2> 
                <div className="flex-centeredColumn contentBlock">
                    <div className="svg-info ">
                        <div className="svg-info-card-3col">
                            <img src={'/product/performance/customize.svg'} alt="Customize" />
                            <h3>Conduct all reviews from one place</h3>
                            <p className="py-1">Coordinate scheduled annual reviews or less formal manager 1-1s from ChartHop</p>
                        </div>                        
                        <div className="svg-info-card-3col">
                            <img src={'/product/performance/results.svg'} alt="Results" />
                            <h3>Analyze performance data alongside all your other people data</h3>
                            <p className="py-1">Drive promotion planning or retention programs by analyzing performance ratings alongside compensation, employee engagement, and more</p>
                        </div>
                        <div className="svg-info-card-3col">
                            <img src={'/product/performance/control.svg'} alt="Control" />
                            <h3>Configure performance reviews with the most relevant questions for your organization</h3>
                            <p className="py-1">Create performance review processes tailored to your organization using pre-built questions or add new questions as needed.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="sectionSpacing">
                <div className="contentBlock">
                    <MultimedialPanel 
                        multimediaPosition="left"
                        title="Conduct continuous performance management"
                        link="/product/2105-refresh/continuous_performance.png"
                        type="image"
                        paddingClass="padding-top-video-two"
                        content={
                            <React.Fragment>
                                <p>Make ChartHop your single source for performance management, from scheduled reviews to weekly manager 1-1s to annual goal-setting. Organize 360 performance reviews including peer reviews, upward feedback, and manager reviews. Support manager 1-1s with standardized forms and a single place for employees and managers to share notes. And record individual goals and OKRs and track progress against them.</p>
                            </React.Fragment>
                        }
                    />                  
                </div>
            </section>

            <section className="sectionSpacing backgroundGradientContainer">
                <div className="contentBlock">
                    <MultimedialPanel 
                        multimediaPosition="right"
                        title="Fuel data-driven decision making"
                        link="/product/2105-refresh/data-sheet-performance.png"
                        type="image"
                        paddingClass="padding-top-video-two"
                        content={
                            <React.Fragment>
                                <p>Power headcount and promotion planning with employee performance rating data, or analyze the engagement of your top-performers -- all from one system. Conducting performance reviews in ChartHop allows you to drill-down into performance alongside any other data collected in ChartHop such as compensation, manager’s performance, eNPS, and more.</p>  
                            </React.Fragment>
                        }
                    />                  
                </div>
            </section>

            <section className="sectionSpacing" >
                <div className="contentBlock">
                    <MultimedialPanel 
                        multimediaPosition="left"
                        title="Tailor performance review processes and questions to your organization's needs"
                        link="/product/2105-refresh/performance-questions.png"
                        type="image"
                        paddingClass="padding-top-video-two"
                        content={
                            <React.Fragment>
                                <p>Create forms with a variety of question types (numeric scales, long text, short text, single select, multiple select, etc.). Deploy different performance review forms and questions for different departments, and manage access to employee responses to any of the reviews with data sensitivity controls.</p>  
                            </React.Fragment>
                        }
                    />                  
                </div>
            </section>

            <section className="sectionSpacing mx-0 mt-0">
                <h4 className="listTitle mt-0 text-center pt-3">All the tools you need for continuous performance management</h4>
                <div className="flex-checkbox-container">
                        <div className="checkmark-item">
                            <img src="/product-overview/icons/checkmark_icon.svg" alt="checkmark"/>
                            <p>360 performance reviews</p>
                        </div>
                        <div className="checkmark-item">
                            <img src="/product-overview/icons/checkmark_icon.svg" alt="checkmark"/>
                            <p>Manager 1-1s</p>
                        </div>
                        <div className="checkmark-item">
                            <img src="/product-overview/icons/checkmark_icon.svg" alt="checkmark"/>
                            <p>OKRs and Goals</p>
                        </div>
                        <div className="checkmark-item">
                            <img src="/product-overview/icons/checkmark_icon.svg" alt="checkmark"/>
                            <p>Configurable review questions and forms</p>
                        </div>
                        <div className="checkmark-item">
                            <img src="/product-overview/icons/checkmark_icon.svg" alt="checkmark"/>
                            <p>Connected with compensation and engagement data</p>
                        </div>
                </div>
            </section>

            {/* LONG FORM */}
            <section name="request-demo " id="request-demo" className="request-demo pt-3 pb-4 pt-md-8 pb-md-8">
                <DemoFormLong source={"platform-performance-demo-bottom"} headertext={HtmlParser('<div class="contactTestimonial pr-md-1"><p class="mb-1 contact-testimonial">"ChartHop helped us replace our mundane, outdated processes - there is nothing else like it. </p><p class="mb-0 contact-testimonial"> I can not imagine what we would do without ChartHop.”</p><div class="authorTag"><img src="/home/quotes/quote_DavidDankenbrink@2x.jpg" class="testimonialAuthor" alt="David Dankenbrink" /><div><p class="contactAuthor"><strong>David Dankenbrink</strong> <br>Vice President, Human Resources @ Remy Cointreau</p></div></div>')}/>
            </section>
        </Layout>
    );
};


export default Home;
